import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import Header from "../../../components/Header/Header";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import ReportDateRangeBar, {
  localYmd,
} from "../../../components/Reports/ReportDateRangeBar";
import { fetchReport } from "../../../store/Slices/ReportSlice";
import { buildReportSummary, fmtReportRs } from "../../../utils/reportSummary";
import { getInvoicePartyName, getInvoiceShoppingWala } from "../../../utils/invoice";
import { isAdminRole } from "../../../utils/appPaths";
import { usePageRefresh } from "../../../hooks/usePageRefresh";
import { formatDateTime } from "../../../utils/dateTime";

const TYPES = [
  { id: "sales", label: "Total Sales", shortLabel: "Sales" },
  { id: "cash_in", label: "Cash In", shortLabel: "Cash In" },
  { id: "cash_out", label: "Cash Out", shortLabel: "Cash Out" },
];

const CASH_OUT_TAB_IDS = [
  "company",
  "expenses",
  "loan_given",
  "loan_out",
  "guest_ret",
  "npi",
  "all",
];

const CASH_IN_TAB_IDS = [
  "customer",
  "guest",
  "loan_in",
  "loan_taken",
  "npi",
  "all",
];

const normalizeCashInTab = (raw) => {
  if (raw === "cust_cash" || raw === "cust_bank") return "customer";
  if (CASH_IN_TAB_IDS.includes(raw)) return raw;
  return "all";
};

const normalizeCashOutTab = (raw) => {
  if (raw === "co_cash" || raw === "co_bank") return "company";
  if (raw === "exp_cash" || raw === "exp_bank") return "expenses";
  if (CASH_OUT_TAB_IDS.includes(raw)) return raw;
  return "all";
};

const resolveReportBranchId = (user = {}) => {
  const raw = user.branchId ?? user.branch;
  if (!raw) return localStorage.getItem("branch") || "";
  if (typeof raw === "object" && raw._id) return String(raw._id);
  return String(raw);
};

const fmtDate = (row) => {
  const unix = Number(row?.date);
  if (Number.isFinite(unix) && unix > 0) return formatDateTime(unix);
  if (row?.f_date) return formatDateTime(row.f_date);
  return "—";
};

const partyOf = (row) => {
  const base =
    getInvoicePartyName(row) ||
    row?.name ||
    row?.customer_name ||
    row?.company_name ||
    row?.party_name ||
    row?.partyName ||
    row?.expenseTypeId?.name ||
    "";
  if (!base) return "—";
  const shoppingWala = getInvoiceShoppingWala(row);
  return shoppingWala ? `${base} / ${shoppingWala}` : base;
};

const formatInvoiceRef = (row) => {
  const manual = row?.invoice_no != null && String(row.invoice_no).trim() !== ""
    ? String(row.invoice_no).trim()
    : "";
  const auto =
    row?.auto_invoice_no != null && row?.auto_invoice_no !== ""
      ? `#${row.auto_invoice_no}`
      : "";
  if (manual && auto) return `${manual} · Auto ${auto}`;
  return manual || auto || "";
};

const refOf = (row) => {
  const inv = formatInvoiceRef(row);
  if (!inv) return "—";
  const page = String(row?.customerId?.page_no ?? row?.page_no ?? "").trim();
  return page ? `${inv} · Pg ${page}` : inv;
};

const amountOf = (row) => {
  // Payments use `amount`; invoices use `total_amount`. Prefer amount when present
  // so a stray total_amount: 0 never hides real payment value.
  if (row?.amount != null && row.amount !== "") {
    return Number(row.amount);
  }
  return Number(row?.total_amount ?? row?.expense ?? row?.payment ?? 0);
};

const isBankPayment = (row) => {
  if (!row || row.isDeleted === true) return false;
  if (Number(row.payment_type) === 2) return true;
  if (Number(row.account_type) === 2) return true;
  if (Number(row.accountId?.type) === 2) return true;
  if (row.bank_name) return true;
  return false;
};

const isCashPayment = (row) => {
  if (!row || row.isDeleted === true) return false;
  if (isBankPayment(row)) return false;
  if (Number(row.payment_type) === 1) return true;
  if (Number(row.account_type) === 1) return true;
  if (Number(row.accountId?.type) === 1) return true;
  return false;
};

const paymentAccountLabel = (row) =>
  row?.account_name ||
  row?.accountId?.account_name ||
  row?.bank_name ||
  row?.into_account ||
  row?.from_account ||
  row?.ledger_account ||
  "";

const accountCellOf = (row, mode = "auto") => {
  const name = paymentAccountLabel(row);
  if (!name) return "—";
  if (mode === "into") return `Into · ${name}`;
  if (mode === "from") return `From · ${name}`;
  const t = String(row?._type || "");
  if (
    t.includes("customer") ||
    t.includes("guest") ||
    t.includes("loan_in") ||
    t.includes("loan_taken") ||
    row?.user_type === "Customer"
  ) {
    return `Into · ${name}`;
  }
  if (
    t.includes("company") ||
    t.includes("expense") ||
    t.includes("loan_out") ||
    t.includes("loan_given") ||
    row?.user_type === "Company"
  ) {
    return `From · ${name}`;
  }
  return name;
};

const itemsLabel = (row) => {
  const items = row?.items;
  if (!Array.isArray(items) || !items.length) {
    const account = paymentAccountLabel(row);
    const base = row?.desc || row?.description || "";
    if (account && base) return `${account} · ${base}`;
    if (account) return `Account: ${account}`;
    return base || "—";
  }
  return items
    .map((it) => {
      const name = it?.name || it?.itemId?.name || "Item";
      const qty = Number(it?.qty || 0);
      const unit = it?.unit || "";
      return `${name}${qty ? ` × ${qty}${unit ? ` ${unit}` : ""}` : ""}`;
    })
    .join(", ");
};

const SummaryTable = ({ rows, activeTab, onSelectTab }) => (
  <div>
    <p className="mb-2 px-0.5 text-xs text-muted max850:block hidden">
      Card pe tap karke tab select karo
    </p>
    {/* Mobile / tablet cards */}
    <div className="hidden max850:grid grid-cols-1 gap-2.5">
      {rows.map((row) => {
        const selectable = Boolean(row.tabId && onSelectTab);
        const isActive = selectable && activeTab === row.tabId;
        const Wrapper = selectable ? "button" : "div";
        return (
          <Wrapper
            key={row.label}
            type={selectable ? "button" : undefined}
            onClick={
              selectable
                ? () => {
                    onSelectTab(row.tabId);
                    window.setTimeout(() => {
                      document
                        .getElementById("report-detail-list")
                        ?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 50);
                  }
                : undefined
            }
            className={`rounded-xl border px-3.5 py-3 shadow-soft text-left touch-manipulation transition-colors w-full ${
              isActive
                ? "border-main bg-brandSoft ring-1 ring-main/20"
                : row.bold
                  ? "border-main/25 bg-brandSoft/50"
                  : "border-line bg-card"
            } ${
              selectable
                ? "cursor-pointer active:scale-[.99] hover:border-main/40"
                : ""
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p
                  className={`text-sm font-quicksand ${
                    isActive || row.bold
                      ? "font-bold text-ink"
                      : "font-semibold text-inkSecondary"
                  }`}
                >
                  {row.label}
                </p>
                {row.count != null ? (
                  <p className="mt-0.5 text-xs text-muted tabular-nums">
                    {row.count} {row.count === 1 ? "entry" : "entries"}
                  </p>
                ) : null}
                {selectable ? (
                  <p
                    className={`mt-1 text-[11px] font-bold ${
                      isActive ? "text-main" : "text-muted"
                    }`}
                  >
                    {isActive ? "Selected ▾" : "Tap to open ▸"}
                  </p>
                ) : null}
              </div>
              <p
                className={`shrink-0 text-base font-bold tabular-nums font-quicksand ${
                  isActive || row.bold ? "text-main" : "text-ink"
                }`}
              >
                {fmtReportRs(row.amount)}
              </p>
            </div>
          </Wrapper>
        );
      })}
    </div>

    {/* Desktop table */}
    <div className="max850:hidden rounded-xl border border-line bg-card shadow-soft overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-main text-white">
              <th className="border border-main px-3 py-2.5 text-left font-bold">
                Particular
              </th>
              <th className="border border-main px-3 py-2.5 text-right font-bold">
                Count
              </th>
              <th className="border border-main px-3 py-2.5 text-right font-bold">
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selectable = Boolean(row.tabId && onSelectTab);
              const isActive = selectable && activeTab === row.tabId;
              return (
                <tr
                  key={row.label}
                  onClick={
                    selectable
                      ? () => {
                          onSelectTab(row.tabId);
                          window.setTimeout(() => {
                            document
                              .getElementById("report-detail-list")
                              ?.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                          }, 50);
                        }
                      : undefined
                  }
                  className={`font-quicksand ${
                    isActive
                      ? "bg-brandSoft"
                      : row.bold
                        ? "bg-brandSoft/40"
                        : ""
                  } ${
                    selectable
                      ? "cursor-pointer hover:bg-subtle/80 transition-colors"
                      : ""
                  }`}
                >
                  <td
                    className={`border border-line px-3 py-2.5 ${
                      isActive || row.bold
                        ? "font-bold text-ink"
                        : "text-inkSecondary"
                    }`}
                  >
                    {row.label}
                    {selectable ? (
                      <span className="ml-2 text-[11px] font-semibold text-main">
                        {isActive ? "▾" : "▸"}
                      </span>
                    ) : null}
                  </td>
                  <td className="border border-line px-3 py-2.5 text-right tabular-nums text-muted">
                    {row.count != null ? row.count : "—"}
                  </td>
                  <td
                    className={`border border-line px-3 py-2.5 text-right tabular-nums font-bold ${
                      isActive || row.bold ? "text-main" : "text-ink"
                    }`}
                  >
                    {fmtReportRs(row.amount)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const DetailTable = ({ title, rows, emptyMessage, accountMode = "auto" }) => {
  const [openId, setOpenId] = useState(null);

  const toggle = (id) => setOpenId((cur) => (cur === id ? null : id));

  return (
    <section>
      <div className="mb-2.5 px-0.5">
        <h2 className="text-base font-bold text-ink font-quicksand">{title}</h2>
        <p className="hidden max850:block text-xs text-muted mt-0.5">
          Card pe tap karke items dekho
        </p>
        <p className="text-xs text-muted mt-0.5 max850:hidden">
          Kis ko / kya hua — row pe click karke items dekho
        </p>
      </div>

      {/* Mobile / tablet cards */}
      <div className="hidden max850:block space-y-2.5">
        {rows.length === 0 ? (
          <div className="rounded-xl border border-line bg-card shadow-soft px-4 py-10 text-center text-sm text-muted">
            {emptyMessage}
          </div>
        ) : (
          rows.map((row) => {
            const id = String(
              row._id || `${row._type}-${row.date}-${row.amount}`
            );
            const open = openId === id;
            const items = Array.isArray(row.items) ? row.items : [];
            const canExpand = items.length > 0 || row.desc || row.description;
            return (
              <article
                key={id}
                className={`rounded-xl border bg-card shadow-soft overflow-hidden transition-colors ${
                  open ? "border-main/30" : "border-line"
                }`}
              >
                <button
                  type="button"
                  onClick={() => canExpand && toggle(id)}
                  className={`w-full text-left px-3.5 py-3 touch-manipulation active:bg-subtle/70 transition-colors ${
                    canExpand ? "cursor-pointer" : "cursor-default"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5 mb-1">
                        <span className="inline-flex text-[11px] font-bold px-2 py-0.5 rounded-md bg-brandSoft text-main">
                          {row._typeLabel || "Entry"}
                        </span>
                        {paymentAccountLabel(row) ? (
                          <span className="inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-md bg-infoSoft text-info">
                            {accountCellOf(row, accountMode)}
                          </span>
                        ) : null}
                        <span className="text-[11px] text-muted tabular-nums">
                          {fmtDate(row)}
                        </span>
                      </div>
                      <p className="text-sm font-bold text-ink leading-snug">
                        {partyOf(row)}
                      </p>
                      <p className="mt-0.5 text-xs text-muted truncate">
                        Ref {refOf(row)}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-base font-bold tabular-nums text-ink">
                        {fmtReportRs(amountOf(row))}
                      </p>
                      {canExpand ? (
                        <span className="mt-1 inline-block text-[11px] font-bold text-main">
                          {open ? "Hide ▾" : "Items ▸"}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-inkSecondary line-clamp-2 leading-relaxed">
                    {itemsLabel(row)}
                  </p>
                </button>

                {open && items.length > 0 ? (
                  <div className="border-t border-line bg-subtle/60 px-3.5 py-2.5">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-muted mb-2">
                      Items
                    </p>
                    <ul className="space-y-2">
                      {items.map((it, idx) => (
                        <li
                          key={it._id || idx}
                          className="flex items-start justify-between gap-3 rounded-lg bg-card border border-line px-2.5 py-2"
                        >
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-ink truncate">
                              {it?.name || it?.itemId?.name || "Item"}
                            </p>
                            <p className="text-[11px] text-muted mt-0.5">
                              Qty {Number(it?.qty || 0)}
                              {it?.unit ? ` ${it.unit}` : ""}
                              {it?.price != null
                                ? ` · @ ${fmtReportRs(it.price)}`
                                : ""}
                            </p>
                          </div>
                          <p className="shrink-0 text-xs font-bold tabular-nums text-ink">
                            {fmtReportRs(it?.amount)}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {open &&
                items.length === 0 &&
                (row.desc || row.description) ? (
                  <div className="border-t border-line bg-subtle/60 px-3.5 py-2.5">
                    <p className="text-xs text-inkSecondary">
                      {row.desc || row.description}
                    </p>
                  </div>
                ) : null}
              </article>
            );
          })
        )}
      </div>

      {/* Desktop table */}
      <div className="max850:hidden rounded-xl border border-line bg-card shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse min-w-[720px]">
            <thead>
              <tr className="bg-main text-white">
                <th className="border border-main px-3 py-2.5 text-left font-bold w-28">
                  Date
                </th>
                <th className="border border-main px-3 py-2.5 text-left font-bold">
                  Type
                </th>
                <th className="border border-main px-3 py-2.5 text-left font-bold">
                  Party / Name
                </th>
                <th className="border border-main px-3 py-2.5 text-left font-bold">
                  Account
                </th>
                <th className="border border-main px-3 py-2.5 text-left font-bold">
                  Ref
                </th>
                <th className="border border-main px-3 py-2.5 text-left font-bold">
                  Detail
                </th>
                <th className="border border-main px-3 py-2.5 text-right font-bold">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="border border-line px-3 py-10 text-center text-muted"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const id = String(
                    row._id || `${row._type}-${row.date}-${row.amount}`
                  );
                  const open = openId === id;
                  const items = Array.isArray(row.items) ? row.items : [];
                  return (
                    <React.Fragment key={id}>
                      <tr
                        className="font-quicksand hover:bg-subtle/60 cursor-pointer transition-colors"
                        onClick={() => toggle(id)}
                      >
                        <td className="border border-line px-3 py-2 whitespace-nowrap text-inkSecondary">
                          {fmtDate(row)}
                        </td>
                        <td className="border border-line px-3 py-2 whitespace-nowrap">
                          <span className="text-xs font-bold px-2 py-0.5 rounded-md bg-subtle text-inkSecondary">
                            {row._typeLabel || "—"}
                          </span>
                        </td>
                        <td className="border border-line px-3 py-2 font-semibold text-ink">
                          {partyOf(row)}
                        </td>
                        <td className="border border-line px-3 py-2 whitespace-nowrap">
                          <span className="text-xs font-bold px-2 py-0.5 rounded-md bg-infoSoft text-info">
                            {accountCellOf(row, accountMode)}
                          </span>
                        </td>
                        <td className="border border-line px-3 py-2 text-inkSecondary">
                          {refOf(row)}
                        </td>
                        <td className="border border-line px-3 py-2 text-inkSecondary max-w-[280px]">
                          <span className="line-clamp-2">{itemsLabel(row)}</span>
                          {(items.length > 0 || row.desc) && (
                            <span className="ml-1 text-main text-xs font-bold">
                              {open ? "▾" : "▸"}
                            </span>
                          )}
                        </td>
                        <td className="border border-line px-3 py-2 text-right tabular-nums font-bold text-ink">
                          {fmtReportRs(amountOf(row))}
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-subtle/50">
                          <td
                            colSpan={7}
                            className="border border-line px-3 py-3"
                          >
                            {items.length > 0 ? (
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs border-collapse">
                                  <thead>
                                    <tr className="text-muted">
                                      <th className="text-left py-1.5 pr-3 font-semibold">
                                        Item
                                      </th>
                                      <th className="text-right py-1.5 px-2 font-semibold">
                                        Qty
                                      </th>
                                      <th className="text-left py-1.5 px-2 font-semibold">
                                        Unit
                                      </th>
                                      <th className="text-right py-1.5 px-2 font-semibold">
                                        Price
                                      </th>
                                      <th className="text-right py-1.5 pl-2 font-semibold">
                                        Amount
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {items.map((it, idx) => (
                                      <tr
                                        key={it._id || idx}
                                        className="border-t border-line/70"
                                      >
                                        <td className="py-1.5 pr-3 font-semibold text-ink">
                                          {it?.name ||
                                            it?.itemId?.name ||
                                            "Item"}
                                        </td>
                                        <td className="py-1.5 px-2 text-right tabular-nums">
                                          {Number(it?.qty || 0)}
                                        </td>
                                        <td className="py-1.5 px-2">
                                          {it?.unit || "—"}
                                        </td>
                                        <td className="py-1.5 px-2 text-right tabular-nums">
                                          {fmtReportRs(it?.price)}
                                        </td>
                                        <td className="py-1.5 pl-2 text-right tabular-nums font-bold">
                                          {fmtReportRs(it?.amount)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="text-sm text-inkSecondary">
                                {row.desc ||
                                  row.description ||
                                  `${row._typeLabel || "Entry"} · ${partyOf(row)} · ${fmtReportRs(amountOf(row))}`}
                              </p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

const PrimaryTabs = ({ tabs, active, onChange, ariaLabel }) => (
  <div
    role="tablist"
    aria-label={ariaLabel}
    className="flex w-full rounded-xl border border-line bg-card p-1 shadow-soft"
  >
    {tabs.map((t) => {
      const isActive = active === t.id;
      return (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={isActive}
          onClick={() => onChange(t.id)}
          className={`relative flex-1 min-h-11 min-w-0 px-1.5 sm:px-3 rounded-lg text-[13px] sm:text-sm font-bold font-quicksand transition-colors touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-main/25 ${
            isActive
              ? "bg-main text-white shadow-soft"
              : "bg-transparent text-muted active:bg-subtle hover:text-inkSecondary"
          }`}
        >
          <span className="hidden sm:inline truncate">{t.label}</span>
          <span className="sm:hidden truncate">
            {t.shortLabel || t.label}
          </span>
        </button>
      );
    })}
  </div>
);

const SubTabs = ({ tabs, active, onChange, ariaLabel }) => {
  const listRef = React.useRef(null);
  const btnRefs = React.useRef({});

  React.useEffect(() => {
    const el = btnRefs.current[active];
    if (!el) return;
    el.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [active]);

  return (
    <div className="relative">
      {/* Edge fades — hint that tabs scroll on mobile */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6 bg-gradient-to-r from-page to-transparent max850:from-page sm:hidden"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 z-10 w-6 bg-gradient-to-l from-page to-transparent max850:from-page sm:hidden"
      />
      <div
        ref={listRef}
        role="tablist"
        aria-label={ariaLabel}
        className="flex items-stretch gap-1 overflow-x-auto overscroll-x-contain scroll-smooth snap-x snap-mandatory touch-pan-x px-1 py-0.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((t) => {
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              ref={(node) => {
                if (node) btnRefs.current[t.id] = node;
                else delete btnRefs.current[t.id];
              }}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange(t.id)}
              className={`snap-start relative shrink-0 inline-flex items-center gap-1.5 min-h-10 px-3 rounded-lg text-[13px] sm:text-sm font-semibold font-quicksand whitespace-nowrap transition-colors touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-main/25 ${
                isActive
                  ? "bg-brandSoft text-main"
                  : "bg-transparent text-muted active:bg-subtle hover:text-inkSecondary"
              }`}
            >
              <span className="hidden sm:inline">{t.label}</span>
              <span className="sm:hidden">{t.shortLabel || t.label}</span>
              {t.count != null ? (
                <span
                  className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-md text-[11px] font-bold tabular-nums leading-none ${
                    isActive
                      ? "bg-main/10 text-main"
                      : "bg-subtle text-muted"
                  }`}
                >
                  {t.count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DailyReportDetail = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const typeParam = params.get("type") || "sales";
  const type = TYPES.some((t) => t.id === typeParam) ? typeParam : "sales";

  const isHqAdmin = isAdminRole();
  const branchId = useMemo(() => {
    // HQ admins: all branches unless URL has a real Mongo branch id
    if (isHqAdmin) {
      const fromUrl = params.get("branchId") || "";
      return /^[a-f\d]{24}$/i.test(fromUrl) ? fromUrl : "";
    }
    const userData = JSON.parse(localStorage.getItem("user") || "{}");
    return resolveReportBranchId(userData);
  }, [isHqAdmin, params]);

  const today = localYmd();
  const [fromDate, setFromDate] = useState(params.get("from") || today);
  const [toDate, setToDate] = useState(params.get("to") || today);
  const [salesTab, setSalesTab] = useState(
    params.get("sale") === "guest"
      ? "guest"
      : params.get("sale") === "credit"
        ? "credit"
        : "all"
  );
  const [cashInTab, setCashInTab] = useState(
    normalizeCashInTab(params.get("cin"))
  );
  const [cashOutTab, setCashOutTab] = useState(
    normalizeCashOutTab(params.get("cout"))
  );

  const dispatch = useDispatch();
  const ReportState = useSelector((state) => state.ReportState);
  const basePath = window.location.pathname.startsWith("/branch")
    ? "/branch"
    : "/admin";

  useEffect(() => {
    if (!toDate || toDate < fromDate) setToDate(fromDate);
  }, [fromDate, toDate]);

  useEffect(() => {
    const next = new URLSearchParams(params);
    next.set("type", type);
    next.set("from", fromDate);
    next.set("to", toDate || fromDate);
    if (branchId) next.set("branchId", branchId);
    if (type === "sales") {
      next.set("sale", salesTab);
      next.delete("cin");
      next.delete("cout");
    } else if (type === "cash_in") {
      next.set("cin", cashInTab);
      next.delete("sale");
      next.delete("cout");
    } else if (type === "cash_out") {
      next.set("cout", cashOutTab);
      next.delete("sale");
      next.delete("cin");
    }
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, fromDate, toDate, branchId, salesTab, cashInTab, cashOutTab]);

  useEffect(() => {
    if (type === "sales") {
      const sale = params.get("sale");
      if (sale === "guest" || sale === "all" || sale === "credit") {
        setSalesTab(sale);
      }
    } else if (type === "cash_in") {
      const cin = params.get("cin");
      if (cin) setCashInTab(normalizeCashInTab(cin));
    } else if (type === "cash_out") {
      const cout = params.get("cout");
      if (cout) setCashOutTab(normalizeCashOutTab(cout));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    const promise = dispatch(
      fetchReport({
        branchId,
        startDate: fromDate,
        endDate: toDate || fromDate,
      })
    );
    return () => {
      promise.abort?.();
    };
  }, [dispatch, branchId, fromDate, toDate]);

  const refresh = useCallback(() => {
    dispatch(
      fetchReport({
        branchId,
        startDate: fromDate,
        endDate: toDate || fromDate,
      })
    );
  }, [dispatch, branchId, fromDate, toDate]);

  usePageRefresh(refresh, [branchId, fromDate, toDate]);

  const summary = useMemo(
    () => buildReportSummary(ReportState?.data),
    [ReportState?.data]
  );

  const data = ReportState?.data || {};
  const transactions = Array.isArray(data.transactions) ? data.transactions : [];
  const guests = Array.isArray(data.guest_transactions)
    ? data.guest_transactions
    : [];
  const payments = Array.isArray(data.payments) ? data.payments : [];
  const expenses = (Array.isArray(data.expenses) ? data.expenses : []).filter(
    (e) => e.isDeleted !== true
  );
  const loans = Array.isArray(data.all_loans)
    ? data.all_loans
    : Array.isArray(data.cash_loans)
      ? data.cash_loans
      : [];
  const loanPays = Array.isArray(data.all_loan_repayments)
    ? data.all_loan_repayments
    : Array.isArray(data.cash_loan_repayments)
      ? data.cash_loan_repayments
      : [];
  const guestReturns = (
    Array.isArray(data.guest_returns) ? data.guest_returns : []
  ).filter((r) => r.isDeleted !== true);

  const nonProductInvoices = (
    Array.isArray(data.non_product_invoices) ? data.non_product_invoices : []
  ).filter((r) => r.isDeleted !== true);

  const npiCashOut = nonProductInvoices.filter((r) => r.is_cash_out);
  const npiCashIn = nonProductInvoices.filter((r) => r.is_cash_in);

  const customerPayments = payments.filter(
    (p) => p.isDeleted !== true && p.user_type === "Customer"
  );
  const companyPayments = payments.filter(
    (p) => p.isDeleted !== true && p.user_type === "Company"
  );
  const tag = (rows, typeLabel, typeKey) =>
    rows.map((r) => ({ ...r, _typeLabel: typeLabel, _type: typeKey }));

  const salesSummaryRows = [
    {
      label: "Total sales",
      amount: summary.total_sales,
      count: summary.invoice_count,
      bold: true,
      tabId: "all",
    },
    {
      label: "Credit (Customer)",
      amount: summary.customer_sales,
      count: transactions.length,
      tabId: "credit",
    },
    {
      label: "Guest (direct / naqdi)",
      amount: summary.guest_sales,
      count: guests.length,
      tabId: "guest",
    },
  ];

  const cashInSummaryRows = [
    {
      label: "Total Cash In",
      amount: summary.money_in_total ?? summary.total_cash_in,
      bold: true,
      tabId: "all",
    },
    {
      label: "Customer payments (Cash + Bank)",
      amount:
        Number(summary.cash_received_from_customers || 0) +
        Number(summary.bank_received_from_customers || 0),
      count: customerPayments.filter(
        (p) => isCashPayment(p) || isBankPayment(p)
      ).length,
      tabId: "customer",
    },
    {
      label: "Guest sales (naqdi)",
      amount: summary.guest_sales,
      count: guests.length,
      tabId: "guest",
    },
    {
      label: "Non-product debit (cash in)",
      amount: summary.npi_cash_in ?? 0,
      count: npiCashIn.length,
      tabId: "npi",
    },
    {
      label: "Loan repayments (in)",
      amount:
        summary.total_loan_repayments_in ?? summary.total_cash_loan_repayments,
      count: loanPays.filter((r) => Number(r.loanType) === 1).length,
      tabId: "loan_in",
    },
    {
      label: "Loans taken (in)",
      amount: summary.total_loans_taken ?? 0,
      count: loans.filter((r) => Number(r.loanType) !== 1).length,
      tabId: "loan_taken",
    },
  ];

  const cashOutSummaryRows = [
    {
      label: "Total Cash Out",
      amount: summary.money_out_total,
      bold: true,
      tabId: "all",
    },
    {
      label: "Company payments (Cash + Bank)",
      amount:
        Number(summary.cash_paid_to_companies || 0) +
        Number(summary.bank_paid_to_companies || 0),
      count: companyPayments.filter(
        (p) => isCashPayment(p) || isBankPayment(p)
      ).length,
      tabId: "company",
    },
    {
      label: "Expenses (Cash + Bank)",
      amount: summary.total_expenses,
      count: expenses.length,
      tabId: "expenses",
    },
    {
      label: "Loans given (out)",
      amount: summary.total_loans_given ?? summary.total_cash_loans_given,
      count: loans.filter((r) => Number(r.loanType) === 1).length,
      tabId: "loan_given",
    },
    {
      label: "Loan repayments (out)",
      amount: summary.total_loan_repayments_out ?? 0,
      count: loanPays.filter((r) => Number(r.loanType) !== 1).length,
      tabId: "loan_out",
    },
    {
      label: "Guest returns",
      amount: summary.total_guest_returns,
      count: guestReturns.length,
      tabId: "guest_ret",
    },
    {
      label: "Non-product credit (cash out)",
      amount: summary.npi_cash_out ?? 0,
      count: npiCashOut.length,
      tabId: "npi",
    },
  ];

  const salesDetailRows = [
    ...tag(transactions, "Credit Sale", "credit"),
    ...tag(guests, "Guest Sale", "guest"),
  ].sort((a, b) => Number(a.date || 0) - Number(b.date || 0));

  const cashInCustomer = tag(
    customerPayments.filter((p) => isCashPayment(p) || isBankPayment(p)),
    "Customer Payment",
    "customer"
  );
  const cashInGuest = tag(guests, "Guest Sale", "guest");
  const cashInLoanIn = tag(
    loanPays.filter((r) => Number(r.loanType) === 1),
    "Loan Repayment In",
    "loan_in"
  );
  const cashInLoanTaken = tag(
    loans.filter((r) => Number(r.loanType) !== 1),
    "Loan Taken",
    "loan_taken"
  );
  const cashInNpi = tag(npiCashIn, "Non-Product Debit", "npi");

  const cashInDetailRows = [
    ...cashInCustomer,
    ...cashInGuest,
    ...cashInLoanIn,
    ...cashInLoanTaken,
    ...cashInNpi,
  ].sort((a, b) => Number(a.date || 0) - Number(b.date || 0));

  const cashInByTab = {
    customer: cashInCustomer,
    guest: cashInGuest,
    loan_in: cashInLoanIn,
    loan_taken: cashInLoanTaken,
    npi: cashInNpi,
    all: cashInDetailRows,
  };

  const cashOutCompany = tag(
    companyPayments.filter((p) => isCashPayment(p) || isBankPayment(p)),
    "Company Payment",
    "company"
  );
  const cashOutExpenses = tag(expenses, "Expense", "expenses");
  const cashOutLoanGiven = tag(
    loans.filter((r) => Number(r.loanType) === 1),
    "Loan Given",
    "loan_given"
  );
  const cashOutLoanOut = tag(
    loanPays.filter((r) => Number(r.loanType) !== 1),
    "Loan Repayment Out",
    "loan_out"
  );
  const cashOutGuestRet = tag(guestReturns, "Guest Return", "guest_ret");
  const cashOutNpi = tag(npiCashOut, "Non-Product Credit", "npi");

  const cashOutDetailRows = [
    ...cashOutCompany,
    ...cashOutExpenses,
    ...cashOutLoanGiven,
    ...cashOutLoanOut,
    ...cashOutGuestRet,
    ...cashOutNpi,
  ].sort((a, b) => Number(a.date || 0) - Number(b.date || 0));

  const cashOutByTab = {
    company: cashOutCompany,
    expenses: cashOutExpenses,
    loan_given: cashOutLoanGiven,
    loan_out: cashOutLoanOut,
    guest_ret: cashOutGuestRet,
    npi: cashOutNpi,
    all: cashOutDetailRows,
  };

  const pageTitle =
    TYPES.find((t) => t.id === type)?.label || "Report detail";
  const dateLabel =
    fromDate === toDate ? fromDate : `${fromDate} → ${toDate}`;

  const setType = (next) => {
    const p = new URLSearchParams(params);
    p.set("type", next);
    setParams(p);
  };

  const isInitialLoading = ReportState.loading && !ReportState.data;

  const salesSubTabs = [
    {
      id: "credit",
      label: "Credit Sale",
      shortLabel: "Credit",
      count: transactions.length,
    },
    {
      id: "guest",
      label: "Guest Sale",
      shortLabel: "Guest",
      count: guests.length,
    },
    {
      id: "all",
      label: "All",
      shortLabel: "All",
      count: transactions.length + guests.length,
    },
  ];

  const cashInSubTabs = [
    {
      id: "customer",
      label: "Customer Payment",
      shortLabel: "Customer",
      count: cashInCustomer.length,
    },
    {
      id: "guest",
      label: "Guest Sale",
      shortLabel: "Guest",
      count: cashInGuest.length,
    },
    {
      id: "loan_in",
      label: "Loan Repay In",
      shortLabel: "Loan In",
      count: cashInLoanIn.length,
    },
    {
      id: "loan_taken",
      label: "Loan Taken",
      shortLabel: "Loan Take",
      count: cashInLoanTaken.length,
    },
    {
      id: "npi",
      label: "Non-Product Debit",
      shortLabel: "NPI In",
      count: cashInNpi.length,
    },
    {
      id: "all",
      label: "All",
      shortLabel: "All",
      count: cashInDetailRows.length,
    },
  ];

  const cashOutSubTabs = [
    {
      id: "company",
      label: "Company Payment",
      shortLabel: "Company",
      count: cashOutCompany.length,
    },
    {
      id: "expenses",
      label: "Expenses",
      shortLabel: "Expense",
      count: cashOutExpenses.length,
    },
    {
      id: "loan_given",
      label: "Loan Given",
      shortLabel: "Loan Out",
      count: cashOutLoanGiven.length,
    },
    {
      id: "loan_out",
      label: "Loan Repay Out",
      shortLabel: "Repay Out",
      count: cashOutLoanOut.length,
    },
    {
      id: "guest_ret",
      label: "Guest Return",
      shortLabel: "Guest Ret",
      count: cashOutGuestRet.length,
    },
    {
      id: "npi",
      label: "Non-Product Credit",
      shortLabel: "NPI Out",
      count: cashOutNpi.length,
    },
    {
      id: "all",
      label: "All",
      shortLabel: "All",
      count: cashOutDetailRows.length,
    },
  ];

  const activeSubTabs =
    type === "sales"
      ? {
          ariaLabel: "Sales type",
          active: salesTab,
          onChange: setSalesTab,
          tabs: salesSubTabs,
        }
      : type === "cash_in"
        ? {
            ariaLabel: "Cash In type",
            active: cashInTab,
            onChange: setCashInTab,
            tabs: cashInSubTabs,
          }
        : {
            ariaLabel: "Cash Out type",
            active: cashOutTab,
            onChange: setCashOutTab,
            tabs: cashOutSubTabs,
          };

  return (
    <BodyWrapper>
      {/* Mobile title — Header is max850:hidden */}
      <div className="hidden max850:block mb-3">
        <button
          type="button"
          onClick={() => navigate(`${basePath}/daily-report`)}
          className="text-sm font-semibold text-main hover:underline mb-1"
        >
          ← Daily Report
        </button>
        <h1 className="text-xl font-extrabold text-ink font-quicksand">
          {pageTitle}
        </h1>
        <p className="text-xs text-muted mt-0.5">{dateLabel}</p>
      </div>

      <Header title={pageTitle} desc={`Table detail · ${dateLabel}`} Back />

      <div className="mb-3 max850:hidden">
        <Link
          to={`${basePath}/daily-report`}
          className="text-sm font-semibold text-main hover:underline"
        >
          ← Back to Daily Report
        </Link>
      </div>

      {/* Tabs scroll with content — not sticky */}
      <div className="mb-2 space-y-2">
        <PrimaryTabs
          ariaLabel="Report detail type"
          tabs={TYPES}
          active={type}
          onChange={setType}
        />

        {!isInitialLoading ? (
          <SubTabs
            ariaLabel={activeSubTabs.ariaLabel}
            active={activeSubTabs.active}
            onChange={activeSubTabs.onChange}
            tabs={activeSubTabs.tabs}
          />
        ) : null}
      </div>

      <div className="mb-3">
        <ReportDateRangeBar
          compact
          fromDate={fromDate}
          toDate={toDate}
          onFromChange={setFromDate}
          onToChange={setToDate}
          refreshing={Boolean(ReportState.refreshing)}
          periodLabel={dateLabel}
        />
      </div>

      {isInitialLoading ? (
        <div className="flex justify-center py-16">
          <FetchingLoading />
        </div>
      ) : (
        <div className="space-y-5">
          <SummaryTable
            rows={
              type === "sales"
                ? salesSummaryRows
                : type === "cash_in"
                  ? cashInSummaryRows
                  : cashOutSummaryRows
            }
            activeTab={
              type === "sales"
                ? salesTab
                : type === "cash_in"
                  ? cashInTab
                  : cashOutTab
            }
            onSelectTab={
              type === "sales"
                ? setSalesTab
                : type === "cash_in"
                  ? setCashInTab
                  : setCashOutTab
            }
          />

          <div id="report-detail-list" className="space-y-5">
          {type === "sales" && (
            <>
              {salesTab === "credit" && (
                <DetailTable
                  title="Credit sales — kis ko / kya items"
                  rows={tag(transactions, "Credit Sale", "credit")}
                  emptyMessage="Is period mein koi credit sale nahi."
                />
              )}
              {salesTab === "guest" && (
                <DetailTable
                  title="Guest sales — kis ko / kya items"
                  rows={tag(guests, "Guest Sale", "guest")}
                  emptyMessage="Is period mein koi guest sale nahi."
                />
              )}
              {salesTab === "all" && (
                <DetailTable
                  title="All sales (Credit + Guest)"
                  rows={salesDetailRows}
                  emptyMessage="Is period mein koi sale nahi."
                />
              )}
            </>
          )}

          {type === "cash_in" && (
            <DetailTable
              title={
                cashInTab === "all"
                  ? "All Cash In entries"
                  : `Cash In — ${
                      {
                        customer: "Customer Payment (Cash + Bank)",
                        guest: "Guest Sale",
                        loan_in: "Loan Repayment In",
                        loan_taken: "Loan Taken",
                        npi: "Non-Product Debit",
                      }[cashInTab] || "detail"
                    }`
              }
              rows={cashInByTab[cashInTab] || []}
              emptyMessage="Is period mein koi cash-in entry nahi."
              accountMode="into"
            />
          )}

          {type === "cash_out" && (
            <DetailTable
              title={
                cashOutTab === "all"
                  ? "All Cash Out entries"
                  : `Cash Out — ${
                      {
                        company: "Company Payment (Cash + Bank)",
                        expenses: "Expenses (Cash + Bank)",
                        loan_given: "Loan Given",
                        loan_out: "Loan Repayment Out",
                        guest_ret: "Guest Return",
                        npi: "Non-Product Credit",
                      }[cashOutTab] || "detail"
                    }`
              }
              rows={cashOutByTab[cashOutTab] || []}
              emptyMessage="Is period mein koi cash-out entry nahi."
              accountMode="from"
            />
          )}
          </div>

          <button
            type="button"
            onClick={() => navigate(`${basePath}/daily-report`)}
            className="text-sm font-semibold text-main hover:underline"
          >
            ← Daily Report pe wapas
          </button>
        </div>
      )}
    </BodyWrapper>
  );
};

export default DailyReportDetail;
